import { useQueryClient, useMutation, useIsMutating } from '@tanstack/react-query';
import { createGenericQuery, getGenericQueryKey } from 'js/react_query_helpers/helpers';
export const useSharedRivieraMutation = (previewKey, previewMetadata, mutationFn, options) => {
    var _a, _b, _c, _d, _e;
    const queryClient = useQueryClient();
    const queryPackage = createGenericQuery({
        queryRouteFilter: ['custom', 'CustomMutation'],
        queryFn: () => async () => ({ apiData: undefined, pkgData: null }),
        getQueryKey: getGenericQueryKey('custom', 'CustomMutation', (arg) => { var _a; return (_a = arg.previewKey) === null || _a === void 0 ? void 0 : _a.serialized; }),
        optionsFn: () => ({ retry: false, cacheTime: Infinity, staleTime: Infinity }),
    });
    const queryResult = queryPackage.useQuery({
        apiArg: { previewKey, previewMetadata },
        pkgArg: null,
    });
    const queryErrorPackage = createGenericQuery({
        queryRouteFilter: ['custom', 'CustomMutationError'],
        queryFn: () => async () => ({ apiData: undefined, pkgData: null }),
        getQueryKey: getGenericQueryKey('custom', 'CustomMutationError', (arg) => { var _a; return (_a = arg.previewKey) === null || _a === void 0 ? void 0 : _a.serialized; }),
        optionsFn: () => ({ retry: false, cacheTime: Infinity, staleTime: Infinity }),
    });
    const queryErrorResult = queryErrorPackage.useQuery({
        apiArg: { previewKey, previewMetadata },
        pkgArg: null,
    });
    const querySuccessPackage = createGenericQuery({
        queryRouteFilter: ['custom', 'CustomMutationSuccess'],
        queryFn: () => async () => ({ apiData: undefined, pkgData: null }),
        getQueryKey: getGenericQueryKey('custom', 'CustomMutationSuccess', (arg) => { var _a; return (_a = arg.previewKey) === null || _a === void 0 ? void 0 : _a.serialized; }),
        optionsFn: () => ({ retry: false, cacheTime: Infinity, staleTime: Infinity }),
    });
    const querySuccessResult = querySuccessPackage.useQuery({
        apiArg: { previewKey, previewMetadata },
        pkgArg: null,
    });
    const mutationKey = ['custom', 'riviera_mutation', previewKey === null || previewKey === void 0 ? void 0 : previewKey.serialized];
    const mutation = useMutation(mutationKey, async (...params) => {
        queryClient.setQueryData(['CustomMutationError', mutationKey], null);
        queryClient.setQueryData(['CustomMutationSuccess', mutationKey], false);
        try {
            const result = await mutationFn(...params);
            queryPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
                apiData: result,
                pkgData: null,
            });
            querySuccessPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
                apiData: true,
                pkgData: null,
            });
            queryErrorPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
                apiData: undefined,
                pkgData: null,
            });
            return result;
        }
        catch (err) {
            const error = err;
            queryPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
                apiData: undefined,
                pkgData: null,
            });
            queryErrorPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
                apiData: error,
                pkgData: null,
            });
            querySuccessPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
                apiData: false,
                pkgData: null,
            });
        }
        return undefined;
    }, options);
    const isLoading = useIsMutating(mutationKey);
    const reset = () => {
        queryPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
            apiData: undefined,
            pkgData: null,
        });
        querySuccessPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
            apiData: undefined,
            pkgData: null,
        });
        queryErrorPackage.setQueryData(queryClient, { previewKey, previewMetadata }, {
            apiData: undefined,
            pkgData: null,
        });
        mutation.reset();
    };
    // The very unfortunate typecast on this result is necessary because of the `error` type. In `UseMutationResult`,
    // the value of `error` is `TError | null`, but that's represented as NonNullable<TError> in some parts of a union
    // type, and `null` in others. The rest of the union type depends on the other values, like `isLoading`. Unless we
    // want very complicated return logic that has to account for all the possible combinations of values, and that
    // has to stay in sync with the React Query types, we have to do this typecast. This means that we could
    // theorhetically return unsupported combinations of values, like `isLoading: true; error: TError`. However, all of
    // these values are controlled locally in this function, so we have a good deal of control over what we return.
    return {
        ...mutation,
        data: (_a = queryResult.data) === null || _a === void 0 ? void 0 : _a.apiData,
        isLoading: !!isLoading,
        error: (_b = queryErrorResult.data) === null || _b === void 0 ? void 0 : _b.apiData,
        isError: !!((_c = queryErrorResult.data) === null || _c === void 0 ? void 0 : _c.apiData),
        isSuccess: (_e = (_d = querySuccessResult.data) === null || _d === void 0 ? void 0 : _d.apiData) !== null && _e !== void 0 ? _e : false,
        reset,
    };
};
//# sourceMappingURL=useSharedRivieraMutation.preenvesnextjs.map
